/**
 * RepeatPracticePage – "Say it back" scaffolded drill.
 *
 * UX
 *   1. GPT-5.2 pre-generates ~8 level-appropriate phrases on session start.
 *   2. For each phrase we show: target text (large), native translation
 *      below it, a "Hear it" button that plays TTS, and a big mic button.
 *   3. Tap mic → record → auto-stop via VAD → /api/stt → we compute a
 *      word-overlap score server-side, show an emoji + label (perfect /
 *      great / good / try again), and auto-advance after a short pause.
 *   4. At the end we show a summary card with average score + attempts.
 *
 * Scoring is intentionally forgiving because the goal is CONFIDENCE, not
 * phonetic correctness. Matching 70% of the expected words = "great".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CaretLeft,
  ArrowClockwise,
  SpeakerHigh,
  SpinnerGap,
  CheckCircle,
  XCircle,
  Microphone,
  Trophy,
  ArrowSquareOut,
  WarningCircle,
} from '@phosphor-icons/react';

import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import useRecorder from '@/hooks/useRecorder';
import BreathingOrb from '@/components/BreathingOrb';
import TappableText from '@/components/TappableText';
import WordDefinitionSheet from '@/components/WordDefinitionSheet';

const LABEL_META = {
  perfect:    { icon: <Trophy size={18} weight="fill" />,      text: 'Perfecto!',    color: '#7CA982', bg: '#E3EEE4' },
  great:      { icon: <CheckCircle size={18} weight="fill" />, text: 'Great job',    color: '#3F8A50', bg: '#E3EEE4' },
  good:       { icon: <CheckCircle size={18} weight="fill" />, text: 'Good effort',  color: '#8A6B0F', bg: '#FFF5D6' },
  try_again:  { icon: <XCircle size={18} weight="fill" />,     text: 'Try again',    color: '#B04A2F', bg: '#FCE7E0' },
};

export default function RepeatPracticePage() {
  const { sessionId: paramSessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sessionId, setSessionId] = useState(paramSessionId || null);
  const [phrases, setPhrases] = useState([]);
  const [index, setIndex] = useState(0);
  const [attempts, setAttempts] = useState([]);   // [{index, label, score, heard}]
  const [status, setStatus] = useState('loading'); // loading | idle | recording | scoring | done
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null); // {label, score, heard}
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const statusRef = useRef('loading');
  useEffect(() => { statusRef.current = status; }, [status]);

  // Word lookup sheet
  const [defWord, setDefWord] = useState(null);
  function openDef(term, context) { setDefWord({ term, context }); }
  function closeDef() { setDefWord(null); }

  // Iframe guard (mic prompt won't work in preview frames)
  const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

  const targetLang = (user?.practice_direction === 'es_to_en') ? 'en' : 'es';

  const finalizeAndScore = useCallback(async () => {
    if (statusRef.current !== 'recording') return;
    setStatus('scoring');
    // eslint-disable-next-line no-use-before-define
    const blob = await recorderRef.current.stop();
    if (!blob || blob.size < 400) { setStatus('idle'); return; }
    try {
      const form = new FormData();
      form.append('audio', blob, 'clip.webm');
      const sttResp = await fetch(`${API}/stt?language=${targetLang}`, {
        method: 'POST',
        credentials: 'include',
        headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {},
        body: form,
      });
      const sttJson = await sttResp.json();
      const heard = (sttJson?.text || '').trim();
      const target = phrases[index]?.target || '';
      const scoreResp = await http.post('/repeat/attempt', {
        session_id: sessionId,
        index,
        target,
        heard,
      });
      const att = scoreResp.data;
      setAttempts((prev) => [...prev.filter((a) => a.index !== att.index), att]);
      setFeedback(att);
      setStatus('idle');
      // Auto-advance after a short pause if the attempt wasn't a "try_again"
      if (att.label !== 'try_again') {
        setTimeout(() => {
          setFeedback(null);
          setIndex((i) => (i < phrases.length - 1 ? i + 1 : i));
        }, 1600);
      }
    } catch (e) {
      setError(apiErrorMessage(e) || 'Could not score attempt');
      setStatus('idle');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, phrases, sessionId, targetLang]);

  const handleAutoStop = useCallback(() => {
    setTimeout(() => finalizeAndScore().catch(() => {}), 0);
  }, [finalizeAndScore]);

  const recorder = useRecorder({ vad: true, onAutoStop: handleAutoStop });
  const recorderRef = useRef(recorder);
  useEffect(() => { recorderRef.current = recorder; }, [recorder]);

  // Load or start a repeat session
  useEffect(() => {
    (async () => {
      try {
        if (sessionId) {
          const { data } = await http.get(`/repeat/${sessionId}`);
          setPhrases(data.phrases || []);
          setAttempts(data.attempts || []);
          setStatus('idle');
        } else {
          setStatus('loading');
          const { data } = await http.post('/repeat/start');
          setSessionId(data.id);
          setPhrases(data.phrases || []);
          setStatus('idle');
          window.history.replaceState({}, document.title, `/repeat/${data.id}`);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 402 && typeof detail === 'object') {
          setError(detail.message || 'Upgrade to continue.');
          const reason = detail.kind || 'repeat_daily_cap';
          setTimeout(() => navigate(`/pricing?reason=${reason}`), 1200);
          return;
        }
        setError(apiErrorMessage(e) || 'Could not load lesson');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Play the current phrase via TTS. */
  async function playPhrase() {
    const p = phrases[index];
    if (!p?.target) return;
    setPlaying(true);
    try {
      // Defensively pause any in-flight audio so we don't double-play. May
      // throw if the audio element was already detached — that's fine.
      try { audioRef.current?.pause(); } catch { /* already stopped */ }
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text: p.target }),
      });
      if (!resp.ok) throw new Error('TTS failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      await new Promise((resolve) => {
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        audio.play().catch(() => resolve());
      });
    } catch (_) {
      // TTS playback failed (network, decode error, autoplay blocked). Not
      // fatal — the target text is already on screen, learner can tap "Hear
      // it" again to retry.
    }
    setPlaying(false);
  }

  async function toggleMic() {
    if (status === 'scoring') return;
    if (inIframe) {
      setError('Microphone is blocked inside this preview window. Open LingoFlow in a new tab.');
      return;
    }
    if (!recorder.recording) {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser does not support microphone access.');
        return;
      }
      try {
        setFeedback(null);
        await recorder.start();
        setStatus('recording');
      } catch (e) {
        const name = e?.name || '';
        setError(name === 'NotAllowedError'
          ? 'Mic permission denied. Click the 🔒 icon in the address bar → Allow microphone.'
          : (e?.message || 'Could not start mic.'));
      }
    } else {
      await finalizeAndScore();
    }
  }

  async function finish() {
    try {
      const { data } = await http.post(`/repeat/${sessionId}/end`);
      setStatus('done');
      setAttempts(data.attempts || attempts);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const orbState = status === 'recording' ? 'listening' : status === 'scoring' ? 'thinking' : 'idle';
  const current = phrases[index];
  const progressPct = phrases.length ? Math.round(((index) / phrases.length) * 100) : 0;
  const avgScore = useMemo(() => {
    if (!attempts.length) return 0;
    return attempts.reduce((s, a) => s + (a.score || 0), 0) / attempts.length;
  }, [attempts]);

  // DONE screen
  if (status === 'done') {
    return (
      <div className="lf-shell px-6 py-8" data-testid="repeat-done">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/home')} className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="repeat-done-back">
            <CaretLeft size={18} />
          </button>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Repeat Practice</div>
            <h1 className="text-2xl font-bold">Lesson complete 🌟</h1>
          </div>
        </div>

        <div className="lf-card mt-6 p-5 text-center">
          <div className="text-5xl font-bold text-[var(--lf-primary)]">{Math.round(avgScore * 100)}%</div>
          <div className="mt-1 text-sm text-[var(--lf-text-muted)]">Average match score</div>
          <div className="mt-2 text-sm">
            {attempts.length}/{phrases.length} phrases attempted
          </div>
        </div>

        <div className="mt-6 space-y-2" data-testid="repeat-done-list">
          {phrases.map((p, i) => {
            const att = attempts.find((a) => a.index === i);
            const meta = att ? LABEL_META[att.label] : null;
            return (
              <div key={i} className="lf-card p-3 flex items-start gap-3">
                <div className="flex-1">
                  <div className="font-semibold">{p.target}</div>
                  <div className="text-xs text-[var(--lf-text-muted)]">{p.native}</div>
                  {att?.heard ? (
                    <div className="mt-1 text-xs italic text-[var(--lf-text-muted)]">you said: “{att.heard}”</div>
                  ) : null}
                </div>
                {meta ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold"
                        style={{ color: meta.color, background: meta.bg }}>
                    {meta.icon} {Math.round((att?.score || 0) * 100)}%
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex gap-3">
          <button onClick={() => navigate('/home')} className="lf-btn lf-btn-ghost flex-1" data-testid="repeat-done-home">
            Home
          </button>
          <button
            onClick={async () => { setFeedback(null); setAttempts([]); setIndex(0); setSessionId(null); setStatus('loading');
              try {
                const { data } = await http.post('/repeat/start');
                setSessionId(data.id); setPhrases(data.phrases || []); setStatus('idle');
                window.history.replaceState({}, document.title, `/repeat/${data.id}`);
              } catch (e) { setError(apiErrorMessage(e)); }
            }}
            className="lf-btn lf-btn-primary flex-[2]"
            data-testid="repeat-again-btn"
          >
            <ArrowClockwise size={16} weight="bold" /> New lesson
          </button>
        </div>
        <div className="h-10" />
      </div>
    );
  }

  // MAIN screen
  return (
    <div className="lf-shell flex flex-col h-screen" data-testid="repeat-page">
      {/* Iframe banner */}
      {inIframe ? (
        <div className="mx-4 mt-4 rounded-2xl border border-[var(--lf-accent)] bg-[#FFF6DB] px-4 py-3 text-sm" data-testid="repeat-iframe-banner">
          <div className="flex items-start gap-2">
            <WarningCircle size={18} weight="fill" className="mt-0.5 text-[#8A6B0F]" />
            <div className="flex-1 text-[#5C4809]">
              <div className="font-semibold">Open in a new tab to use your mic</div>
              <button
                onClick={() => { try { window.open(window.location.href, '_blank', 'noopener,noreferrer'); } catch { /* popup */ } }}
                className="lf-btn lf-btn-primary mt-2 w-full py-2 text-sm"
                data-testid="repeat-open-new-tab"
              >
                <ArrowSquareOut size={14} weight="bold" /> Open LingoFlow
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6">
        <button onClick={() => navigate('/home')} className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="repeat-back">
          <CaretLeft size={18} />
        </button>
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Repeat practice</div>
          <div className="text-sm font-semibold">{targetLang === 'es' ? 'Spanish' : 'English'}</div>
        </div>
        <button onClick={finish} className="lf-chip lf-chip-primary" data-testid="repeat-finish-btn">
          Finish
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-6 mt-4">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--lf-surface-muted)]" data-testid="repeat-progress">
          <motion.div
            className="h-full bg-[var(--lf-primary)]"
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <div className="mt-1 text-xs text-[var(--lf-text-muted)] text-right">
          {Math.min(index + 1, phrases.length || 1)} / {phrases.length || '…'}
        </div>
      </div>

      {/* Phrase card */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {status === 'loading' || !current ? (
          <div className="text-center text-[var(--lf-text-muted)]" data-testid="repeat-loading">
            <SpinnerGap size={28} className="mx-auto animate-spin text-[var(--lf-primary)]" />
            <div className="mt-2">Crafting your lesson…</div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lf-card p-6 text-center"
              data-testid={`repeat-card-${index}`}
            >
              <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Say this out loud</div>
              <div className="mt-3 text-3xl font-bold text-[var(--lf-text)]" data-testid="repeat-target">
                <TappableText text={current.target} onWordTap={openDef} context={current.target} />
              </div>
              <div className="mt-2 text-base text-[var(--lf-text-muted)]" data-testid="repeat-native">
                {current.native}
              </div>
              {current.respelling ? (
                <div className="mt-2 inline-block rounded-full bg-[#FFF5EE] px-3 py-1 text-sm font-semibold text-[var(--lf-primary)]">
                  Say it like: <span className="font-bold">{current.respelling}</span>
                </div>
              ) : null}
              {current.ipa ? (
                <div className="mt-1 text-xs font-mono text-[var(--lf-text-muted)]">
                  IPA /{current.ipa}/
                </div>
              ) : null}
              {current.tip ? (
                <div className="mt-2 text-xs italic text-[var(--lf-text-muted)]">💡 {current.tip}</div>
              ) : current.hint ? (
                <div className="mt-2 inline-block rounded-full bg-[var(--lf-surface-muted)] px-3 py-1 text-xs text-[var(--lf-secondary-foreground)]">
                  pronounce: {current.hint}
                </div>
              ) : null}
              <button
                onClick={playPhrase}
                disabled={playing}
                className="lf-btn lf-btn-secondary mt-5"
                data-testid="repeat-hear-btn"
              >
                {playing ? <SpinnerGap size={16} className="animate-spin" /> : <SpeakerHigh size={16} weight="fill" />}
                Hear it
              </button>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Feedback flash */}
        <AnimatePresence>
          {feedback ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-4 flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-semibold"
              style={{
                background: LABEL_META[feedback.label]?.bg || '#EEE',
                color: LABEL_META[feedback.label]?.color || '#333',
              }}
              data-testid={`repeat-feedback-${feedback.label}`}
            >
              <span className="inline-flex items-center gap-2">
                {LABEL_META[feedback.label]?.icon}
                {LABEL_META[feedback.label]?.text} — {Math.round((feedback.score || 0) * 100)}%
              </span>
              {feedback.heard ? (
                <span className="truncate italic opacity-80">“{feedback.heard}”</span>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>

        {error ? <div className="mt-4 text-sm text-[var(--lf-destructive)] text-center">{error}</div> : null}
      </div>

      {/* Mic orb + advance */}
      <div className="pb-8 pt-2 flex flex-col items-center">
        <button
          onClick={toggleMic}
          disabled={status === 'scoring' || !current}
          className="focus:outline-none"
          aria-label="Toggle microphone"
          data-testid="repeat-mic-btn"
        >
          <BreathingOrb
            state={orbState}
            size={170}
            level={recorder.level}
            label={
              status === 'recording' ? (recorder.voiceDetected ? 'Listening…' : 'Say it now') :
              status === 'scoring'   ? 'Scoring…' :
                                       'Tap to repeat'
            }
            sublabel={status === 'recording' ? 'Auto-stops when you pause' : undefined}
          />
        </button>

        {index < phrases.length - 1 ? (
          <button
            onClick={() => { setFeedback(null); setIndex((i) => i + 1); }}
            className="mt-3 text-[12px] font-semibold text-[var(--lf-text-muted)] hover:text-[var(--lf-primary)]"
            data-testid="repeat-skip-btn"
          >
            Skip this phrase →
          </button>
        ) : (
          <button
            onClick={finish}
            className="lf-btn lf-btn-primary mt-3 py-2 text-sm"
            data-testid="repeat-wrap-btn"
          >
            See your results <CheckCircle size={14} weight="fill" />
          </button>
        )}
      </div>

      <WordDefinitionSheet
        open={!!defWord}
        term={defWord?.term}
        context={defWord?.context}
        onClose={closeDef}
        practiceDirection={user?.practice_direction}
      />
    </div>
  );
}
